import { livekitConfig } from './config'

/**
 * LiveKit Token Service
 *
 * NOTE: In production, token generation should happen on a secure backend
 * because it requires the API secret. For development/demo purposes,
 * we're generating tokens client-side.
 *
 * For production, move this to a Supabase Edge Function.
 */

// Token payload structure (matches LiveKit JWT spec)
interface TokenGrant {
  roomJoin?: boolean
  room?: string
  canPublish?: boolean
  canSubscribe?: boolean
  canPublishData?: boolean
  hidden?: boolean
  recorder?: boolean
}

interface TokenPayload {
  iss: string // API Key
  sub: string // Participant identity
  name?: string // Participant name
  exp: number // Expiration timestamp
  nbf: number // Not before timestamp
  video?: TokenGrant
}

// Base64URL encode (without padding)
function base64UrlEncode(str: string): string {
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

// HMAC-SHA256 signature (using Web Crypto API)
async function signHmacSha256(message: string, secret: string): Promise<string> {
  const encoder = new TextEncoder()
  const keyData = encoder.encode(secret)
  const messageData = encoder.encode(message)

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData)

  // Convert ArrayBuffer to base64url
  const signatureArray = new Uint8Array(signature)
  let binary = ''
  for (let i = 0; i < signatureArray.length; i++) {
    binary += String.fromCharCode(signatureArray[i])
  }

  return base64UrlEncode(binary)
}

/**
 * Generate a LiveKit access token for meeting rooms
 *
 * @param roomName - The room to join
 * @param participantName - Display name for the participant
 * @param participantIdentity - Unique identifier for the participant
 * @param options - Additional options
 * @returns JWT token string
 */
export async function generateMeetingToken(
  roomName: string,
  participantName: string,
  participantIdentity: string,
  options: {
    canPublish?: boolean
    canSubscribe?: boolean
    ttlSeconds?: number
  } = {}
): Promise<string> {
  const {
    canPublish = true,
    canSubscribe = true,
    ttlSeconds = 7200, // 2 hours default for meetings
  } = options

  const apiKey = livekitConfig.apiKey
  const apiSecret = livekitConfig.apiSecret

  if (!apiKey || !apiSecret) {
    throw new Error('LiveKit API key and secret are required. Set VITE_LIVEKIT_API_KEY and VITE_LIVEKIT_API_SECRET.')
  }

  const now = Math.floor(Date.now() / 1000)

  const payload: TokenPayload = {
    iss: apiKey,
    sub: participantIdentity,
    name: participantName,
    exp: now + ttlSeconds,
    nbf: now,
    video: {
      roomJoin: true,
      room: roomName,
      canPublish,
      canSubscribe,
      canPublishData: true,
    },
  }

  // Create JWT
  const header = { alg: 'HS256', typ: 'JWT' }
  const headerEncoded = base64UrlEncode(JSON.stringify(header))
  const payloadEncoded = base64UrlEncode(JSON.stringify(payload))

  const message = `${headerEncoded}.${payloadEncoded}`
  const signature = await signHmacSha256(message, apiSecret)

  return `${message}.${signature}`
}

/**
 * Generate a unique room ID
 */
export function generateRoomId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * Generate a shareable room link (public, no login required)
 */
export function generateRoomLink(roomId: string): string {
  const baseUrl = window.location.origin
  return `${baseUrl}/meeting/${roomId}`
}

/**
 * Generate a unique participant identity
 */
export function generateParticipantIdentity(): string {
  return `user_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`
}

export const tokenService = {
  generateMeetingToken,
  generateRoomId,
  generateRoomLink,
  generateParticipantIdentity,
}
